import React, { FC, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { EvalSample } from "@tsmono/inspect-common/types";
import {
  buildSelectableMessageIndex,
  ChatView,
} from "@tsmono/inspect-components/chat";
import { MetaDataGrid } from "@tsmono/inspect-components/content";
import { resolveSelected } from "@tsmono/inspect-components/selection";
import {
  flatTree,
  selectedEventNodes,
  TranscriptVirtualList,
  useEventNodes,
} from "@tsmono/inspect-components/transcript";
import { ModelTokenTable } from "@tsmono/inspect-components/usage";
import {
  Card,
  CardBody,
  CardHeader,
  NoContentsPanel,
} from "@tsmono/react/components";
import type { VirtualListHandle } from "@tsmono/react/virtual";

import {
  kSampleInfoTabId,
  kSampleJsonTabId,
  kSampleMessagesTabId,
  kSampleScoringTabId,
  kSampleTranscriptTabId,
  sampleTabAlias,
} from "../../../constants";
import {
  useSelectedEvalSampleData,
  useSelectedLogDetails,
} from "../../../state/hooks";
import { formatDateTime, formatTime } from "../../../utils/format";
import { useLogRouteParams } from "../../routing/url";
import { SampleJSONView } from "../SampleJSONView";
import { SampleScoresView } from "../scores/SampleScoresView";

import { PrintHeading } from "./PrintHeading";
import styles from "./SamplePrintView.module.css";

/**
 * Print route page component.
 * Renders sample content without virtualization for printing.
 * URL pattern: /logs/<logPath>/samples/sample/<id>/<epoch>/print?view=<tab>
 * Repeated `events=<node id>` params restrict a transcript print to those
 * events (ids as the transcript assigns them: the uuid, or the position-based
 * fallback for logs without uuids). Repeated `messages=<id>` params restrict
 * a messages print to those messages (the message id, or the position-based
 * `msg-<n>` fallback for logs without ids).
 */
export const SamplePrintView: FC = () => {
  const { sampleId, epoch } = useLogRouteParams();
  const [searchParams] = useSearchParams();
  const view = sampleTabAlias(
    searchParams.get("view") ?? kSampleTranscriptTabId
  );
  const printingSelection = searchParams.has("events");
  const printingMessageSelection =
    searchParams.has("messages") && view === kSampleMessagesTabId;

  // Get sample data
  const sample = useSelectedEvalSampleData().sample;

  const evalSpec = useSelectedLogDetails()?.eval;

  // Transcript: process events through the same pipeline, all expanded
  const sampleEvents = sample?.events || [];
  const { eventNodes } = useEventNodes(sampleEvents, false);
  const flattenedNodes = useMemo(() => {
    const all = flatTree(eventNodes, null);
    const ids = searchParams.getAll("events");
    return ids.length > 0 ? selectedEventNodes(all, new Set(ids)) : all;
  }, [eventNodes, searchParams]);
  // A stale or hand-edited selection URL: say so instead of printing a page
  // with nothing on it.
  const selectionNotFound =
    printingSelection &&
    view === kSampleTranscriptTabId &&
    sampleEvents.length > 0 &&
    flattenedNodes.length === 0;
  const listHandle = useRef<VirtualListHandle | null>(null);

  // Messages: restrict to the selected rows when `messages=<id>` params are
  // present, resolving against the same fold the Messages tab uses. A row is
  // a head message plus its folded tool messages, so the header counts rows.
  const sampleMessages = sample?.messages ?? [];
  const messageIds = searchParams.getAll("messages");
  const selectedMessageRows =
    messageIds.length > 0
      ? resolveSelected(
          buildSelectableMessageIndex(sampleMessages),
          new Set(messageIds)
        )
      : undefined;
  const printMessages = selectedMessageRows?.flat() ?? sampleMessages;
  const messageSelectionNotFound =
    printingMessageSelection &&
    sampleMessages.length > 0 &&
    selectedMessageRows?.length === 0;

  // Auto-print once content has finished rendering.
  // Uses a MutationObserver to detect when the DOM stops changing,
  // then triggers print after a settling period.
  const contentRef = useRef<HTMLDivElement>(null);
  const hasPrinted = useRef(false);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (
      !sample ||
      hasPrinted.current ||
      !contentRef.current ||
      selectionNotFound ||
      messageSelectionNotFound
    ) {
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    const triggerPrint = () => {
      if (hasPrinted.current) return;
      hasPrinted.current = true;
      observer.disconnect();
      window.focus();
      window.print();
      window.close();
    };

    const observer = new MutationObserver(() => {
      // Reset the timer every time the DOM changes
      clearTimeout(timer);
      timer = setTimeout(triggerPrint, 500);
    });

    observer.observe(contentRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Start the initial timer (in case nothing mutates, e.g. empty content)
    timer = setTimeout(triggerPrint, 1000);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [sample, selectionNotFound, messageSelectionNotFound]);

  if (!sample) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading sample data...</div>
      </div>
    );
  }

  return (
    <div className={styles.container} ref={contentRef}>
      <div className={styles.header}>
        <PrintHeading evalSpec={evalSpec} />
        {sampleId && epoch && (
          <div className={styles.sampleInfo}>
            Sample {sampleId} (Epoch {epoch})
            {printingSelection
              ? ` · ${flattenedNodes.length} selected ${flattenedNodes.length === 1 ? "event" : "events"}`
              : null}
            {printingMessageSelection && selectedMessageRows
              ? ` · ${selectedMessageRows.length} selected ${selectedMessageRows.length === 1 ? "message" : "messages"}`
              : null}
          </div>
        )}
      </div>

      {selectionNotFound && (
        <NoContentsPanel text="None of the selected events were found in this sample." />
      )}

      {view === kSampleTranscriptTabId && !selectionNotFound && (
        <TranscriptVirtualList
          id="print-transcript"
          listHandle={listHandle}
          eventNodes={flattenedNodes}
          disableVirtualization={true}
        />
      )}

      {view === kSampleMessagesTabId && !messageSelectionNotFound && (
        <ChatView
          id="print-messages"
          messages={printMessages}
          display={{
            indented: true,
            unlabeledRoles: ["assistant"],
            formatDateTime,
          }}
        />
      )}

      {messageSelectionNotFound && (
        <NoContentsPanel text="None of the selected messages were found in this sample." />
      )}

      {view === kSampleScoringTabId && (
        <SampleScoresView sample={sample} scrollRef={contentRef} />
      )}

      {view === kSampleInfoTabId && <PrintMetadata sample={sample} />}

      {view === kSampleJsonTabId && <SampleJSONView sample={sample} />}
    </div>
  );
};

/**
 * Renders sample metadata using MetadataGrid (non-virtualized)
 * instead of RecordTree for print-friendly output.
 */
const PrintMetadata: FC<{ sample: EvalSample }> = ({ sample }) => {
  const sampleMetadatas: React.JSX.Element[] = [];

  if (sample.invalidation) {
    const formatTimestamp = (timestamp: string) => {
      try {
        return formatDateTime(new Date(timestamp));
      } catch {
        return timestamp;
      }
    };

    const invalidationRecord: Record<string, unknown> = {};
    if (sample.invalidation.author) {
      invalidationRecord["Author"] = sample.invalidation.author;
    }
    if (sample.invalidation.timestamp) {
      invalidationRecord["Timestamp"] = formatTimestamp(
        sample.invalidation.timestamp
      );
    }
    if (sample.invalidation.reason) {
      invalidationRecord["Reason"] = sample.invalidation.reason;
    }
    if (
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      sample.invalidation.metadata &&
      Object.keys(sample.invalidation.metadata).length > 0
    ) {
      invalidationRecord["Metadata"] = sample.invalidation.metadata;
    }
    sampleMetadatas.push(
      <Card key="print-invalidation">
        <CardHeader label="Invalidation" />
        <CardBody>
          <MetaDataGrid entries={invalidationRecord} />
        </CardBody>
      </Card>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (sample.model_usage && Object.keys(sample.model_usage).length > 0) {
    sampleMetadatas.push(
      <Card key="print-usage">
        <CardHeader label="Usage" />
        <CardBody>
          <ModelTokenTable model_usage={sample.model_usage} />
        </CardBody>
      </Card>
    );
  }

  if (
    sample.total_time !== undefined &&
    sample.total_time !== null &&
    sample.working_time !== undefined &&
    sample.working_time !== null
  ) {
    sampleMetadatas.push(
      <Card key="print-time">
        <CardHeader label="Time" />
        <CardBody>
          <MetaDataGrid
            entries={{
              Working: formatTime(sample.working_time),
              Total: formatTime(sample.total_time),
            }}
          />
        </CardBody>
      </Card>
    );
  }

  if (Object.keys(sample.metadata).length > 0) {
    sampleMetadatas.push(
      <Card key="print-metadata">
        <CardHeader label="Metadata" />
        <CardBody>
          <MetaDataGrid entries={sample.metadata} />
        </CardBody>
      </Card>
    );
  }

  if (Object.keys(sample.store).length > 0) {
    sampleMetadatas.push(
      <Card key="print-store">
        <CardHeader label="Store" />
        <CardBody>
          <MetaDataGrid entries={sample.store} />
        </CardBody>
      </Card>
    );
  }

  if (sampleMetadatas.length === 0) {
    return <NoContentsPanel text="No sample metadata available" />;
  }

  return <div>{sampleMetadatas}</div>;
};
